import React, { useMemo } from "react";
import { valueToString } from "helpers/Helpers";
import classNames from "classnames";
import BaseReportTable from "./BaseReportTable";

import "components/reportTable/reportTable.scss";
import { formatReportCurrency } from "helpers/reportFormat";

interface IProps {
  data: Array<Record<string, any>>;
  columns: Array<Record<string, any>>;
  description: string;
  compileDate: string;
}

export default function CashReconciliationTable(props: IProps) {
  const tableData = useMemo(
    () => props.data?.map(row => (row.is_footer ? { ...row, is_footer: false } : row)) ?? [],
    [props.data],
  );

  function formatDisplay(type: string, value: string) {
    let newValue = value !== null && value !== undefined ? value : "";

    if (!isNaN(Number(newValue)) && newValue !== "") {
      switch (type) {
        case "currency":
          newValue = formatReportCurrency(value);
          break;
        case "decimal":
          newValue = Number(value).toFixed(2);
          break;
        case "percentage":
          newValue = `${Number(value) * 100}%`;
          break;
        default:
          break;
      }
    } else if (type === "string" && newValue !== "") {
      newValue = valueToString(value).replace(/(^\w|\s\w)/g, c => c.toUpperCase());
    }

    return newValue;
  }

  // Format cell data based on column
  function cellData(columnId: string, cellValue: string, displayType: string, cell?: any) {
    const display = formatDisplay(displayType, cellValue);

    if (columnId === "payment_type") {
      const depth = Number(cell?.row?.original?.depth ?? 0);
      if (depth > 0) {
        return <span style={{ paddingLeft: `${depth * 1.25}rem`, display: "inline-block" }}>{display}</span>;
      }
    }

    return display;
  }

  // Add on click events based on column
  function cellOnClick() {
    return {};
  }

  function rowClassNames(row: any) {
    return classNames({
      "report-table-row": row.original.type === "subRow" || row.original.type === "method",
      "report-table-row-total": row.original.type === "total",
      "report-table-row-method": row.original.type === "method",
    });
  }

  function cellClassNames(cell: any, isRowLocked: boolean) {
    return classNames({
      "report-table-row-locked": isRowLocked,
    });
  }

  // Add footer data based on column
  function footerData(columnId: string, footerRow?: Record<string, any>) {
    if (!footerRow) {
      return null;
    }

    switch (columnId) {
      case "payment_type":
        return footerRow?.payment_type as string;

      case "amount":
      case "fee":
        return formatDisplay(
          "currency",
          String(footerRow[columnId] ?? 0),
        );

      default:
        return footerRow[columnId] as string ?? null;
    }
  }

  return (
    <BaseReportTable
      data={tableData}
      columns={props.columns}
      description={props.description}
      compileDate={props.compileDate}
      cellOnClick={cellOnClick}
      cellData={cellData}
      cellClassNames={cellClassNames}
      rowClassNames={rowClassNames}
      footerData={footerData}
      hideFooter
      expanding={false}
    />
  );
}
